<h1 class="text-center border-bottom pb-3">Product import status</h1>

<div
  *ngIf="!hasProductFactoryAccess"
  class="alert alert-warning mt-3"
>
  <h2 class="h5 mb-2">Alleen voor Product Factory</h2>
  <p class="mb-0">Raak deze pagina niet aan tenzij je onderdeel bent van het Product Factory-team (AWS-PF).</p>
</div>

<ng-container *ngIf="hasProductFactoryAccess">
  <div [ngClass]="{ 'd-none': feedbackSettings.show }">
    <app-import-stages-diagram
      [areas]="areas"
      class="d-block mt-3 mb-2"
    ></app-import-stages-diagram>

    <div class="d-flex justify-content-between align-items-center px-1 py-3">
      <p class="mb-0">
        <ng-container *ngIf="!editMode"
          >Importstatus per gebied. Klik op een tegel voor het meest recente rapport; klik op het klok-icoon voor de
          rapportgeschiedenis.</ng-container
        >
        <ng-container *ngIf="editMode"
          >Sleep de tegels of gebruik de pijlknoppen om de volgorde te wijzigen. Opslaan bewaart de
          volgorde.</ng-container
        >
      </p>

      <div class="d-flex gap-2">
        <ng-container *ngIf="!editMode">
          <button
            class="btn btn-sm btn-outline-primary"
            type="button"
            (click)="enterEditMode()"
            [disabled]="isLoading || areas.length === 0"
          >
            Bewerken
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            (click)="loadBoard()"
            [disabled]="isLoading"
          >
            <span
              *ngIf="isLoading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Vernieuwen
          </button>
        </ng-container>

        <ng-container *ngIf="editMode">
          <button
            class="btn btn-sm btn-primary"
            type="button"
            (click)="save()"
            [disabled]="!isDirty() || isSaving"
          >
            <span
              *ngIf="isSaving"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Opslaan
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            type="button"
            (click)="cancelEdit()"
            [disabled]="isSaving"
          >
            Annuleren
          </button>
        </ng-container>
      </div>
    </div>

    <div
      class="row g-3"
      *ngIf="!isLoading"
      cdkDropList
      cdkDropListOrientation="mixed"
      [cdkDropListDisabled]="!editMode"
      (cdkDropListDropped)="drop($event)"
    >
      <div
        #tile
        class="col-12 col-sm-6 col-lg-4 col-xxl-3"
        *ngFor="let area of areas; let i = index"
        cdkDrag
        [cdkDragDisabled]="!editMode"
      >
        <div
          class="card h-100 border-2 position-relative"
          [class.tile-editing]="editMode"
          [class.tile-clickable]="!editMode && area.latestReportUrl"
          [ngClass]="tileBorderClass(area)"
        >
          <!-- Whole-tile click opens the most recent report in a new tab. -->
          <a
            *ngIf="!editMode && area.latestReportUrl"
            class="stretched-link"
            [href]="area.latestReportUrl"
            target="_blank"
            rel="noopener"
            [attr.aria-label]="'Open meest recente rapport voor ' + area.displayName"
          ></a>
          <div class="card-body d-flex flex-column">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="d-flex align-items-start me-2">
                <span
                  *ngIf="editMode"
                  class="tile-drag-handle me-2"
                  cdkDragHandle
                  aria-label="Sleep om te verplaatsen"
                  >⠿</span
                >
                <h5 class="card-title mb-0">{{ area.displayName }}</h5>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span [ngClass]="statusBadgeClass(area)">{{ statusLabel(area) }}</span>
                <a
                  *ngIf="!editMode"
                  class="tile-history-link"
                  [routerLink]="['/product-import/status', area.code]"
                  [queryParams]="{ name: area.displayName }"
                  title="Toon importgeschiedenis"
                  aria-label="Toon importgeschiedenis"
                >
                  <i class="fa-solid fa-clock-rotate-left"></i>
                </a>
              </div>
            </div>

            <ng-container *ngIf="area.counts; else neverRun">
              <p class="mb-1">
                <strong>Fouten:</strong>
                <span [class.text-danger]="area.counts.errorCount > 0" [class.fw-bold]="area.counts.errorCount > 0">{{
                  area.counts.errorCount
                }}</span>
                &nbsp;|&nbsp; <strong>Waarschuwingen:</strong>
                <span [class.text-warnings]="area.counts.warningCount > 0" [class.fw-bold]="area.counts.warningCount > 0">{{
                  area.counts.warningCount
                }}</span>
              </p>
              <p class="mb-1 text-muted small">Verwerkt: {{ area.counts.processed }}</p>
              <p class="mb-0 text-muted small mt-auto">
                Laatste run: {{ area.latestBusinessDate }}
                <span *ngIf="area.latestRunNumber !== null">(#{{ area.latestRunNumber }})</span>
                <span *ngIf="area.latestFinishedAt"> — {{ area.latestFinishedAt | utcDate: 'dd-MM-yyyy HH:mm' }}</span>
              </p>
            </ng-container>
            <ng-template #neverRun>
              <p class="mb-0 text-muted small mt-auto">Nog niet uitgevoerd</p>
            </ng-template>

            <div
              *ngIf="editMode"
              class="btn-group btn-group-sm mt-2 align-self-end"
              role="group"
            >
              <button
                type="button"
                class="btn btn-outline-secondary"
                aria-label="Naar links"
                [disabled]="!canMove(i, 'left')"
                (click)="moveTile(i, 'left')"
              >
                ←
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary"
                aria-label="Naar boven"
                [disabled]="!canMove(i, 'up')"
                (click)="moveTile(i, 'up')"
              >
                ↑
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary"
                aria-label="Naar onder"
                [disabled]="!canMove(i, 'down')"
                (click)="moveTile(i, 'down')"
              >
                ↓
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary"
                aria-label="Naar rechts"
                [disabled]="!canMove(i, 'right')"
                (click)="moveTile(i, 'right')"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<div
  class="d-flex justify-content-center feedback_box"
  [ngClass]="{
    'd-none': !feedbackSettings.show,
  }"
>
  <app-feedback
    [(settings)]="feedbackSettings"
    (update)="returnHome()"
  ></app-feedback>
</div>

<div [ngClass]="{ 'd-none': !isLoading }">
  <app-loading-gears></app-loading-gears>
</div>
